/**
 * Bucketing helpers for analytics properties.
 *
 * WHY BUCKETS AND NOT RAW VALUES: a raw byte count is close to a fingerprint —
 * an exact size identifies a specific file, and paired with a timestamp it can
 * single out one transfer of one document. The privacy contract
 * (`docs/analytics/event-spec.md` §0) allows counts about USAGE, not
 * measurements of the user's CONTENT. A bucket answers "are people sending
 * photos or movies?" without ever describing the file itself.
 *
 * Defined ONCE here, for every surface, on purpose: if the extension and
 * Android each invented their own edges, the same transfer would land in
 * different buckets and the resulting distribution would be meaningless. The
 * Kotlin side mirrors these boundaries (see `FtAnalyticsBuckets.kt`); the two
 * must be changed together or cross-surface comparisons silently break.
 *
 * Bucket labels are stable wire values, NOT display strings — they are the
 * registry enums in `packages/core/analytics/events.json`. Renaming one splits
 * its history in PostHog, so treat them as append-only.
 */

/** `size_bucket` for `file_transfer_started`. */
export type SizeBucket = "lt_1mb" | "1_10mb" | "10_100mb" | "gte_100mb";

/** `duration_bucket` for `file_transfer_completed`. */
export type DurationBucket = "lt_10s" | "10_60s" | "1_5m" | "5_30m" | "gte_30m";

const MB = 1024 * 1024;

/** Bucket a byte count. Negative/NaN clamp to the smallest bucket rather than throwing — analytics must never break a transfer. */
export function sizeBucket(bytes: number): SizeBucket {
  if (!Number.isFinite(bytes) || bytes < 10 * MB) {
    return bytes >= MB ? "1_10mb" : "lt_1mb";
  }
  if (bytes < 100 * MB) return "10_100mb";
  return "gte_100mb";
}

/**
 * Bucket an elapsed-milliseconds value.
 *
 * Only meaningful for a transfer that ran inside ONE live session. A resumed
 * transfer's wall-clock spans closed tabs and overnight pauses, so the caller
 * must omit `duration_bucket` entirely when `resumed` is true rather than
 * report a number that measures how long the user was asleep.
 */
export function durationBucket(ms: number): DurationBucket {
  if (!Number.isFinite(ms) || ms < 10_000) return "lt_10s";
  if (ms < 60_000) return "10_60s";
  if (ms < 5 * 60_000) return "1_5m";
  if (ms < 30 * 60_000) return "5_30m";
  return "gte_30m";
}
